import React, { useEffect, useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import moment from "moment"
import API from "@/common/API"
import ActivityLogPage from "@/pages/ActivityLogPage"
import { parsedUser } from "@/common/GetCurrentUser"
import { isSuperAdmin } from "@/common/helpers"

interface OrgRecord {
  id: string
  name: string
  active?: boolean
  totalPlaces?: number
  totalUsers?: number
  totalVehicles?: number
}

interface OrgUserRecord {
  id: string
  firstName: string
  lastName: string
  email: string
  active: boolean
  roles: string[]
}

interface OrgRoleRecord {
  id: string
  name: string
  organizationId: string
  customers?: number
  invoice?: number
  quotes?: number
  inventory?: number
  reports?: number
  organizationUsers?: number
}

interface EmailRecord {
  id: string
  subject: string
  to: string
  from: string
  date: string
  openCount?: number
  content?: string
  userId?: string
}

interface OrgDetailsModalProps {
  show: boolean
  org: OrgRecord | null
  onHide: () => void
  onRefresh?: () => void
}

const OrgDetailsModal: React.FC<OrgDetailsModalProps> = ({ show, org, onHide, onRefresh }) => {
  const { t } = useTranslation("global")
  const currentUser: any = parsedUser()
  const canManageIngestion = isSuperAdmin(currentUser)

  const [activeTab, setActiveTab] = useState<"analysis" | "users" | "roles" | "activity" | "emails" | "ingestion">("analysis")

  // Emails management state
  const [emails, setEmails] = useState<EmailRecord[]>([])
  const [isLoadingEmails, setIsLoadingEmails] = useState(false)
  const [emailsPage, setEmailsPage] = useState(0)
  const [emailsTotalPages, setEmailsTotalPages] = useState(1)
  const [emailsTotalRecords, setEmailsTotalRecords] = useState(0)
  const [emailsSearch, setEmailsSearch] = useState("")
  const [previewEmail, setPreviewEmail] = useState<EmailRecord | null>(null)

  // Ingestion configuration state (SuperAdmin only)
  const [allOrgs, setAllOrgs] = useState<OrgRecord[]>([])
  const [ingestionSources, setIngestionSources] = useState<any[]>([])
  const [selectedNewSourceOrgId, setSelectedNewSourceOrgId] = useState<string>("")
  const [newSourceEnabled, setNewSourceEnabled] = useState<boolean>(true)
  const [isLoadingIngestion, setIsLoadingIngestion] = useState(false)
  const [isAddingSource, setIsAddingSource] = useState(false)
  const [togglingSourceId, setTogglingSourceId] = useState<string | null>(null)
  const [deletingSourceId, setDeletingSourceId] = useState<string | null>(null)
  const [triggeringSourceId, setTriggeringSourceId] = useState<string | null>(null)
  const [ingestionMessage, setIngestionMessage] = useState<string | null>(null)
  const [ingestionError, setIngestionError] = useState<string | null>(null)

  // Users management state
  const [users, setUsers] = useState<OrgUserRecord[]>([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [showUserFormModal, setShowUserFormModal] = useState(false)
  const [editingUser, setEditingUser] = useState<OrgUserRecord | null>(null)

  // Roles management state
  const [roles, setRoles] = useState<OrgRoleRecord[]>([])
  const [isLoadingRoles, setIsLoadingRoles] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [editingRole, setEditingRole] = useState<OrgRoleRecord | null>(null)
  const [roleName, setRoleName] = useState("")
  const [permCustomers, setPermCustomers] = useState(1)
  const [permInvoice, setPermInvoice] = useState(1)
  const [permQuotes, setPermQuotes] = useState(1)
  const [permInventory, setPermInventory] = useState(1)
  const [permReports, setPermReports] = useState(1)
  const [permUsers, setPermUsers] = useState(1)
  const [isSavingRole, setIsSavingRole] = useState(false)

  // User form state
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [selectedRole, setSelectedRole] = useState("Agent")
  const [isActive, setIsActive] = useState(true)
  const [isSavingUser, setIsSavingUser] = useState(false)
  const [userFormError, setUserFormError] = useState<string | null>(null)

  // Org stats state
  const [orgStats, setOrgStats] = useState<any>(null)
  const [isLoadingStats, setIsLoadingStats] = useState(false)

  const loadOrgUsers = async () => {
    if (!org?.id) return
    try {
      setIsLoadingUsers(true)
      const res = await API.getAction("users/List", `OrganizationId=${org.id}&Quantity=200`)
      if (res.data.status === "ok") {
        setUsers(res.data.response || [])
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingUsers(false)
    }
  }

  const loadOrgRoles = async () => {
    if (!org?.id) return
    try {
      setIsLoadingRoles(true)
      const res = await API.getAction("Organization/Roles", `OrganizationID=${org.id}`)
      if (res.data.status === "ok") {
        setRoles(res.data.response || [])
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingRoles(false)
    }
  }

  const loadOrgStats = async () => {
    if (!org?.id) return
    try {
      setIsLoadingStats(true)
      const res = await API.getAction("dashboard/general", `organizationId=${org.id}`)
      if (res.data.status === "ok") {
        setOrgStats(res.data.response)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingStats(false)
    }
  }

  const loadIngestionConfig = async () => {
    if (!org?.id) return
    setIsLoadingIngestion(true)
    try {
      // 1. Fetch current ingestion sources for target org
      try {
        const cfgRes = await API.getAction("Organization/GetIngestionConfigs", `organizationId=${org.id}`)
        if (cfgRes?.data?.status === "ok" && Array.isArray(cfgRes.data.response)) {
          setIngestionSources(cfgRes.data.response)
        } else {
          const fallbackCfg = await API.getAction("Organization/GetIngestionConfig", `organizationId=${org.id}`)
          if (fallbackCfg?.data?.status === "ok" && fallbackCfg.data.response?.sourceOrganizationId) {
            setIngestionSources([fallbackCfg.data.response])
          } else {
            setIngestionSources([])
          }
        }
      } catch (cfgErr) {
        console.warn("Could not load ingestion configs:", cfgErr)
      }

      // 2. Fetch candidate source organizations (with fallback)
      let rawOrgs: any[] = []
      try {
        const orgsRes = await API.getAction("Organization/IngestionSourceOrganizations")
        if (orgsRes?.data?.status === "ok" && Array.isArray(orgsRes.data.response) && orgsRes.data.response.length > 0) {
          rawOrgs = orgsRes.data.response
        }
      } catch (orgErr) {
        console.warn("Could not load IngestionSourceOrganizations, falling back to AdminList:", orgErr)
      }

      if (rawOrgs.length === 0) {
        try {
          const fallbackRes = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
          if (fallbackRes?.data?.status === "ok" && Array.isArray(fallbackRes.data.response)) {
            rawOrgs = fallbackRes.data.response
          }
        } catch (fallbackErr) {
          console.warn("Could not load AdminList fallback:", fallbackErr)
        }
      }

      const normalizedOrgs: OrgRecord[] = rawOrgs.map((o: any) => ({
        id: String(o.id || o.Id || o.ID || ""),
        name: String(o.name || o.Name || o.id || o.Id || ""),
      }))

      setAllOrgs(normalizedOrgs)
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingIngestion(false)
    }
  }

  const handleAddIngestionSource = async () => {
    if (!org?.id || !selectedNewSourceOrgId) return
    try {
      setIsAddingSource(true)
      setIngestionMessage(null)
      setIngestionError(null)
      const res = await API.postAction("Organization/SaveIngestionSource", {
        targetOrganizationId: org.id,
        sourceOrganizationId: selectedNewSourceOrgId,
        enabled: newSourceEnabled
      })
      if (res.data.status === "ok") {
        setSelectedNewSourceOrgId("")
        setIngestionMessage(t("Fuente de ingesta agregada correctamente."))
        await loadIngestionConfig()
      } else {
        setIngestionError(res.data.message || t("Error al agregar la fuente de ingesta."))
      }
    } catch (ex: any) {
      setIngestionError(ex.message || t("Error al agregar la fuente de ingesta."))
    } finally {
      setIsAddingSource(false)
    }
  }

  const handleToggleIngestionSource = async (sourceOrgId: string, currentEnabled: boolean) => {
    if (!org?.id) return
    try {
      setTogglingSourceId(sourceOrgId)
      setIngestionMessage(null)
      setIngestionError(null)
      const res = await API.postAction("Organization/SaveIngestionSource", {
        targetOrganizationId: org.id,
        sourceOrganizationId: sourceOrgId,
        enabled: !currentEnabled
      })
      if (res.data.status === "ok") {
        setIngestionSources(prev => prev.map(s => s.sourceOrganizationId === sourceOrgId ? { ...s, enabled: !currentEnabled } : s))
      } else {
        setIngestionError(res.data.message || t("Error al cambiar el estado de la ingesta."))
      }
    } catch (ex: any) {
      setIngestionError(ex.message || t("Error al cambiar el estado de la ingesta."))
    } finally {
      setTogglingSourceId(null)
    }
  }

  const handleDeleteIngestionSource = async (sourceOrgId: string, sourceName: string) => {
    if (!org?.id) return
    if (!window.confirm(t("¿Seguro que deseas eliminar la sincronización automática de puntos desde '{{name}}'?", { name: sourceName || sourceOrgId }))) {
      return
    }
    try {
      setDeletingSourceId(sourceOrgId)
      setIngestionMessage(null)
      setIngestionError(null)
      const res = await API.postAction("Organization/DeleteIngestionSource", {
        targetOrganizationId: org.id,
        sourceOrganizationId: sourceOrgId
      })
      if (res.data.status === "ok") {
        setIngestionMessage(t("Fuente de ingesta eliminada correctamente."))
        setIngestionSources(prev => prev.filter(s => s.sourceOrganizationId !== sourceOrgId))
      } else {
        setIngestionError(res.data.message || t("Error al eliminar la fuente de ingesta."))
      }
    } catch (ex: any) {
      setIngestionError(ex.message || t("Error al eliminar la fuente de ingesta."))
    } finally {
      setDeletingSourceId(null)
    }
  }

  const handleTriggerIngestion = async (sourceOrgId?: string) => {
    if (!org?.id) return
    try {
      setTriggeringSourceId(sourceOrgId || "all")
      setIngestionMessage(null)
      setIngestionError(null)
      const res = await API.postAction("Organization/TriggerIngestion", {
        targetOrganizationId: org.id,
        sourceOrganizationId: sourceOrgId || undefined
      })
      const respData = res.data.response || {}
      if (res.data.status === "ok" || respData.success) {
        setIngestionMessage(respData.message || t("Ingesta completada correctamente."))
        await loadIngestionConfig()
        loadOrgStats()
      } else {
        setIngestionError(respData.message || res.data.message || t("Error en ingesta."))
      }
    } catch (ex: any) {
      setIngestionError(ex.message || t("Error al ejecutar la ingesta."))
    } finally {
      setTriggeringSourceId(null)
    }
  }

  const loadOrgEmails = async (page = 0, search = emailsSearch) => {
    if (!org?.id) return
    try {
      setIsLoadingEmails(true)
      const qs = `OrganizationId=${org.id}&Page=${page}&Quantity=20${search ? `&Search=${encodeURIComponent(search)}` : ""}`
      const res = await API.getAction("Organization/EmailHistory", qs)
      if (res.data?.status === "ok") {
        setEmails(res.data.response || [])
        setEmailsTotalPages(res.data.quantityOfPages || 1)
        setEmailsTotalRecords(res.data.quantityOfRecords || 0)
        setEmailsPage(page)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingEmails(false)
    }
  }

  useEffect(() => {
    if (show && org?.id) {
      loadOrgUsers()
      loadOrgStats()
      loadOrgRoles()
      loadIngestionConfig()
      loadOrgEmails(0, "")
    }
  }, [show, org?.id])

  useEffect(() => {
    if (show && org?.id && activeTab === "emails") {
      const timer = setTimeout(() => {
        loadOrgEmails(0, emailsSearch)
      }, 350)
      return () => clearTimeout(timer)
    }
  }, [emailsSearch, activeTab, show, org?.id])

  useEffect(() => {
    if (show && org?.id && activeTab === "ingestion" && allOrgs.length === 0) {
      loadIngestionConfig()
    }
  }, [activeTab, show, org?.id, allOrgs.length])

  const handleOpenAddUser = () => {
    setEditingUser(null)
    setFirstName("")
    setLastName("")
    setEmail("")
    setPassword("")
    setSelectedRole("Agent")
    setIsActive(true)
    setUserFormError(null)
    setShowUserFormModal(true)
  }

  const handleOpenEditUser = (u: OrgUserRecord) => {
    setEditingUser(u)
    setFirstName(u.firstName)
    setLastName(u.lastName)
    setEmail(u.email)
    setPassword("")
    setSelectedRole(u.roles?.[0] || "Agent")
    setIsActive(u.active)
    setUserFormError(null)
    setShowUserFormModal(true)
  }

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!org?.id) return

    try {
      setIsSavingUser(true)
      setUserFormError(null)

      if (editingUser) {
        const payload = {
          id: editingUser.id,
          firstName,
          lastName,
          email,
          active: isActive,
          organizationId: org.id,
          role: selectedRole,
        }
        const res = await API.postAction("Account/Edit", payload)
        if (res.data.status === "ok") {
          setShowUserFormModal(false)
          loadOrgUsers()
          if (onRefresh) onRefresh()
          return
        }
        setUserFormError(res.data.response || t("Imposible actualizar usuario"))
      } else {
        const payload = {
          firstName,
          lastName,
          email,
          password,
          organizationId: org.id,
          role: selectedRole,
        }
        const res = await API.postAction("Account/Register", payload)
        if (res.data.status === "ok") {
          setShowUserFormModal(false)
          loadOrgUsers()
          if (onRefresh) onRefresh()
          return
        }
        setUserFormError(res.data.response || t("Imposible registrar usuario"))
      }
    } catch (ex: any) {
      setUserFormError(ex.message || t("Error de conexión"))
    } finally {
      setIsSavingUser(false)
    }
  }

  const handleOpenAddRole = () => {
    setEditingRole(null)
    setRoleName("")
    setPermCustomers(1)
    setPermInvoice(1)
    setPermQuotes(1)
    setPermInventory(1)
    setPermReports(1)
    setPermUsers(1)
    setShowRoleModal(true)
  }

  const handleOpenEditRole = (r: OrgRoleRecord) => {
    setEditingRole(r)
    setRoleName(r.name)
    setPermCustomers(r.customers ?? 1)
    setPermInvoice(r.invoice ?? 1)
    setPermQuotes(r.quotes ?? 1)
    setPermInventory(r.inventory ?? 1)
    setPermReports(r.reports ?? 1)
    setPermUsers(r.organizationUsers ?? 1)
    setShowRoleModal(true)
  }

  const handleTogglePerm = async (r: OrgRoleRecord, field: keyof OrgRoleRecord) => {
    if (!currentUser?.ID) return
    try {
      const updated = {
        ...r,
        [field]: r[field] === 1 ? 0 : 1,
        organizationId: org?.id,
      }
      const res = await API.postAction(`Organization/AddEditRole?CurrentUserId=${currentUser.ID}`, updated)
      if (res.data) {
        loadOrgRoles()
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!org?.id || !roleName.trim() || !currentUser?.ID) return
    try {
      setIsSavingRole(true)
      const payload = {
        id: editingRole?.id || null,
        name: roleName,
        organizationId: org.id,
        customers: permCustomers,
        invoice: permInvoice,
        quotes: permQuotes,
        inventory: permInventory,
        reports: permReports,
        organizationUsers: permUsers,
      }
      await API.postAction(`Organization/AddEditRole?CurrentUserId=${currentUser.ID}`, payload)
      setShowRoleModal(false)
      loadOrgRoles()
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsSavingRole(false)
    }
  }

  if (!org) return null

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered backdrop="static" className="org-details-modal">
        <Modal.Header closeButton className="border-bottom py-3">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-primary bg-opacity-10 p-2 rounded-3">
              <i className="fas fa-building text-primary fs-4" />
            </div>
            <div>
              <Modal.Title className="fw-bold h5 mb-0">{org.name}</Modal.Title>
              <div className="d-flex align-items-center gap-2 mt-1">
                <small className="text-muted">ID: {org.id}</small>
                <Badge bg={org.active !== false ? "success" : "secondary"} pill>
                  {org.active !== false ? t("Activo") : t("Inactivo")}
                </Badge>
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-0">
          {/* Navigation Subtabs */}
          <div className="bg-light border-bottom px-4 pt-3">
            <div className="nav nav-tabs border-bottom-0 gap-1">
              <button
                className={`nav-link ${activeTab === "analysis" ? "active fw-bold" : ""}`}
                onClick={() => setActiveTab("analysis")}
              >
                <i className="fas fa-chart-pie me-2 text-primary" />
                {t("Análisis y Resumen")}
              </button>
              <button
                className={`nav-link ${activeTab === "users" ? "active fw-bold" : ""}`}
                onClick={() => setActiveTab("users")}
              >
                <i className="fas fa-users me-2 text-success" />
                {t("Usuarios y Gestión")}
                <Badge bg="secondary" pill className="ms-2">{users.length}</Badge>
              </button>
              <button
                className={`nav-link ${activeTab === "roles" ? "active fw-bold" : ""}`}
                onClick={() => setActiveTab("roles")}
              >
                <i className="fas fa-user-shield me-2 text-warning" />
                {t("Roles y Permisos")}
              </button>
              <button
                className={`nav-link ${activeTab === "activity" ? "active fw-bold" : ""}`}
                onClick={() => setActiveTab("activity")}
              >
                <i className="fas fa-history me-2 text-info" />
                {t("Actividad de la Organización")}
              </button>
              <button
                className={`nav-link ${activeTab === "emails" ? "active fw-bold" : ""}`}
                onClick={() => setActiveTab("emails")}
              >
                <i className="fas fa-envelope-open-text me-2 text-primary" />
                {t("Correos y Notificaciones")}
                {emailsTotalRecords > 0 && <Badge bg="secondary" pill className="ms-2">{emailsTotalRecords}</Badge>}
              </button>
              {canManageIngestion && (
                <button
                  className={`nav-link ${activeTab === "ingestion" ? "active fw-bold" : ""}`}
                  onClick={() => setActiveTab("ingestion")}
                >
                  <i className="fas fa-file-import me-2 text-danger" />
                  {t("Ingesta Automática")}
                </button>
              )}
            </div>
          </div>

          <div className="p-4">
            {/* Tab 1: Analysis & Overview */}
            {activeTab === "analysis" && (
              <div>
                <Row className="g-3 mb-4">
                  {[
                    { label: t("Lugares"), value: org.totalPlaces ?? orgStats?.totalPlaces ?? 0, icon: "fa-map-location-dot", color: "primary" },
                    { label: t("Usuarios"), value: users.length || (org.totalUsers ?? 0), icon: "fa-users", color: "success" },
                    { label: t("Vehículos"), value: org.totalVehicles ?? 0, icon: "fa-car", color: "warning" },
                    { label: t("Conteo de Visitas"), value: orgStats?.totalVisits ?? "—", icon: "fa-route", color: "info" },
                  ].map((stat, i) => (
                    <Col xs={6} md={3} key={i}>
                      <Card className="border shadow-sm h-100">
                        <Card.Body className="d-flex align-items-center gap-3">
                          <div className={`rounded-circle d-flex align-items-center justify-content-center bg-${stat.color} bg-opacity-10`} style={{ width: 44, height: 44 }}>
                            <i className={`fas ${stat.icon} text-${stat.color}`} />
                          </div>
                          <div>
                            <div className="text-muted small">{stat.label}</div>
                            <div className="fw-bold fs-5">{stat.value}</div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Card className="border shadow-sm">
                  <Card.Header className="bg-white fw-semibold py-2 border-bottom">
                    <i className="fas fa-circle-info me-2 text-muted" />
                    {t("Resumen General de la Organización")}
                  </Card.Header>
                  <Card.Body className="small">
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="text-muted mb-1">{t("Nombre de la Organización")}</div>
                        <div className="fw-bold fs-6">{org.name}</div>
                      </Col>
                      <Col md={6}>
                        <div className="text-muted mb-1">{t("Estado")}</div>
                        <div>
                          <Badge bg={org.active !== false ? "success" : "danger"}>
                            {org.active !== false ? t("Activa y Operativa") : t("Deshabilitada")}
                          </Badge>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* Tab 2: Users & Management */}
            {activeTab === "users" && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 className="fw-bold mb-0">{t("Usuarios de la Organización")}</h6>
                    <small className="text-muted">{t("Gestión de cuentas, roles y estado para esta organización")}</small>
                  </div>
                  <Button variant="success" size="sm" onClick={handleOpenAddUser}>
                    <i className="fas fa-user-plus me-1" />
                    {t("Nuevo Usuario")}
                  </Button>
                </div>

                {isLoadingUsers ? (
                  <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                ) : users.length === 0 ? (
                  <Alert variant="light" className="text-center py-4 border">
                    {t("No se encontraron usuarios para esta organización.")}
                  </Alert>
                ) : (
                  <Table hover responsive className="align-middle small border mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t("Nombre del Usuario")}</th>
                        <th>{t("Correo")}</th>
                        <th>{t("Roles")}</th>
                        <th className="text-center">{t("Estado")}</th>
                        <th className="text-end">{t("Acciones")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.id}>
                          <td className="fw-bold">{u.firstName} {u.lastName}</td>
                          <td>{u.email}</td>
                          <td>
                            {u.roles?.map(r => (
                              <Badge bg="info" text="dark" key={r} className="me-1">{r}</Badge>
                            ))}
                          </td>
                          <td className="text-center">
                            <Badge bg={u.active ? "success" : "secondary"}>
                              {u.active ? t("Activo") : t("Inactivo")}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <Button variant="outline-secondary" size="sm" className="px-2 py-0" onClick={() => handleOpenEditUser(u)}>
                              <i className="fas fa-user-gear me-1" />
                              {t("Editar")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            )}

            {/* Tab 3: Roles & Permissions (Interactive & Editable) */}
            {activeTab === "roles" && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 className="fw-bold mb-0">{t("Matriz de Roles y Permisos")}</h6>
                    <small className="text-muted">{t("Configura y personaliza los permisos de acceso para")} <strong>{org.name}</strong></small>
                  </div>
                  <Button variant="warning" size="sm" className="text-dark fw-semibold" onClick={handleOpenAddRole}>
                    <i className="fas fa-plus me-1" />
                    {t("Nuevo Rol")}
                  </Button>
                </div>

                {isLoadingRoles ? (
                  <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                ) : roles.length === 0 ? (
                  <Alert variant="light" className="text-center py-4 border rounded-3">
                    {t("No se encontraron roles personalizados configurados todavía.")}
                    <div className="mt-2">
                      <Button variant="outline-warning" size="sm" className="text-dark" onClick={handleOpenAddRole}>
                        <i className="fas fa-plus me-1" /> {t("Crear Primer Rol")}
                      </Button>
                    </div>
                  </Alert>
                ) : (
                  <Table hover responsive className="align-middle small border rounded-3 mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>ROL</th>
                        <th className="text-center">CLIENTES</th>
                        <th className="text-center">FACTURACIÓN</th>
                        <th className="text-center">COTIZACIONES</th>
                        <th className="text-center">INVENTARIO</th>
                        <th className="text-center">REPORTES</th>
                        <th className="text-center">USUARIOS ORG</th>
                        <th className="text-end pe-3">ACCIONES</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map(r => (
                        <tr key={r.id}>
                          <td>
                            <Badge bg="primary" className="px-2 py-1">{r.name}</Badge>
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="switch"
                              checked={r.customers === 1}
                              onChange={() => handleTogglePerm(r, "customers")}
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="switch"
                              checked={r.invoice === 1}
                              onChange={() => handleTogglePerm(r, "invoice")}
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="switch"
                              checked={r.quotes === 1}
                              onChange={() => handleTogglePerm(r, "quotes")}
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="switch"
                              checked={r.inventory === 1}
                              onChange={() => handleTogglePerm(r, "inventory")}
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="switch"
                              checked={r.reports === 1}
                              onChange={() => handleTogglePerm(r, "reports")}
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="switch"
                              checked={r.organizationUsers === 1}
                              onChange={() => handleTogglePerm(r, "organizationUsers")}
                            />
                          </td>
                          <td className="text-end pe-3">
                            <Button variant="outline-secondary" size="sm" className="py-0 px-2" onClick={() => handleOpenEditRole(r)}>
                              <i className="fas fa-edit me-1" /> {t("Editar")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            )}

            {/* Tab 4: Activity Log */}
            {activeTab === "activity" && (
              <div>
                <ActivityLogPage
                  embedded
                  compact
                  initialOrganizationId={org.id}
                  lockOrganizationId
                />
              </div>
            )}

            {/* Tab: Emails & Notifications */}
            {activeTab === "emails" && (
              <div>
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                  <div>
                    <h6 className="fw-bold mb-0">{t("Historial de Correos y Notificaciones")}</h6>
                    <small className="text-muted">
                      {t("Registro de correos enviados a usuarios, agentes y contactos de")} <strong>{org.name}</strong> ({emailsTotalRecords} {t("registros")})
                    </small>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      size="sm"
                      placeholder={t("Buscar por asunto o destinatario...")}
                      style={{ maxWidth: 260 }}
                      value={emailsSearch}
                      onChange={e => setEmailsSearch(e.target.value)}
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => loadOrgEmails(emailsPage, emailsSearch)}
                      title={t("Recargar")}
                    >
                      <i className="fas fa-rotate" />
                    </Button>
                  </div>
                </div>

                {isLoadingEmails ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span className="small text-muted">{t("Cargando historial de correos...")}</span>
                  </div>
                ) : emails.length === 0 ? (
                  <Alert variant="light" className="text-center py-5 border rounded-3 text-muted small">
                    <i className="fas fa-inbox fa-2x mb-2 text-muted d-block" />
                    <div>{t("No se encontraron envíos de correo para esta organización.")}</div>
                  </Alert>
                ) : (
                  <>
                    <Table hover responsive className="align-middle small border rounded-3 mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-3">{t("FECHA DE ENVÍO")}</th>
                          <th>{t("DESTINATARIO")}</th>
                          <th>{t("ASUNTO")}</th>
                          <th>{t("REMITENTE")}</th>
                          <th className="text-center">{t("APERTURAS")}</th>
                          <th className="text-end pe-3">{t("ACCIONES")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emails.map(m => (
                          <tr key={m.id}>
                            <td className="ps-3 fw-semibold text-dark">
                              <i className="far fa-clock me-1 text-primary" />
                              {m.date ? moment(m.date).format("DD/MM/YYYY HH:mm") : "—"}
                            </td>
                            <td>
                              <Badge bg="light" text="dark" className="border font-monospace">
                                {m.to}
                              </Badge>
                            </td>
                            <td className="fw-bold text-dark">{m.subject || t("(Sin asunto)")}</td>
                            <td className="text-muted">{m.from || "Sistema Shearly"}</td>
                            <td className="text-center">
                              <Badge bg={m.openCount && m.openCount > 0 ? "success" : "secondary"} pill>
                                {m.openCount || 0} {t("lecturas")}
                              </Badge>
                            </td>
                            <td className="text-end pe-3">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="py-0 px-2"
                                title={t("Ver Detalles")}
                                onClick={() => setPreviewEmail(m)}
                              >
                                <i className="fas fa-eye me-1" />
                                {t("Detalles")}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {emailsTotalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                        <small className="text-muted">
                          {t("Página")} {emailsPage + 1} {t("de")} {emailsTotalPages}
                        </small>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={emailsPage === 0 || isLoadingEmails}
                            onClick={() => loadOrgEmails(emailsPage - 1, emailsSearch)}
                          >
                            <i className="fas fa-chevron-left me-1" />
                            {t("Anterior")}
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={emailsPage >= emailsTotalPages - 1 || isLoadingEmails}
                            onClick={() => loadOrgEmails(emailsPage + 1, emailsSearch)}
                          >
                            {t("Siguiente")}
                            <i className="fas fa-chevron-right ms-1" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Tab 5: Auto Ingestion Multi-Source (SuperAdmin only) */}
            {activeTab === "ingestion" && canManageIngestion && (
              <div className="d-flex flex-column gap-3">
                <Alert variant="warning" className="d-flex align-items-start gap-2 mb-0 shadow-sm">
                  <i className="fas fa-user-shield fs-5 mt-1 text-warning" />
                  <div>
                    <strong className="d-block mb-1">{t("Atribución Automática al Usuario del Sistema ('system')")}:</strong>
                    {t("Para evitar controversia o disputas de autoría entre usuarios de distintas organizaciones, la ingesta automática de puntos desde cualquier organización origen asignará automáticamente el usuario creador al ")}
                    <strong>{t("Usuario del Sistema ('system')")}</strong>.
                  </div>
                </Alert>

                {ingestionMessage && <Alert variant="success" dismissible onClose={() => setIngestionMessage(null)}>{ingestionMessage}</Alert>}
                {ingestionError && <Alert variant="danger" dismissible onClose={() => setIngestionError(null)}>{ingestionError}</Alert>}

                {/* Card 1: Add New Ingestion Source */}
                <Card className="border shadow-sm">
                  <Card.Header className="bg-light fw-semibold d-flex align-items-center justify-content-between py-2">
                    <div>
                      <i className="fas fa-plus-circle me-2 text-primary" />
                      {t("Vincular Nueva Organización Origen")}
                    </div>
                    <Badge bg="danger">
                      <i className="fas fa-shield-halved me-1" />
                      {t("Exclusivo Super Admin")}
                    </Badge>
                  </Card.Header>
                  <Card.Body className="small">
                    <Row className="g-3 align-items-end">
                      <Col md={7}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">{t("Organización Origen (Levantamiento de Puntos)")} *</Form.Label>
                          <Form.Select
                            size="sm"
                            value={selectedNewSourceOrgId}
                            onChange={e => setSelectedNewSourceOrgId(e.target.value)}
                          >
                            <option value="">-- {t("Seleccionar Organización Origen")} --</option>
                            {allOrgs
                              .filter(o => o.id && o.id.toLowerCase() !== (org.id || "").toLowerCase() && !ingestionSources.some(s => s.sourceOrganizationId?.toLowerCase() === o.id.toLowerCase()))
                              .map(o => (
                                <option key={o.id} value={o.id}>
                                  {o.name} ({o.id})
                                </option>
                              ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            {t("Los puntos levantados en esta organización se replicarán a {{target}}.", { target: org.name })}
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group className="mb-2">
                          <Form.Label className="fw-semibold d-block">{t("Estado Inicial")}</Form.Label>
                          <Form.Check
                            type="switch"
                            id="new-source-enabled-switch"
                            label={newSourceEnabled ? t("Activa") : t("Pausada")}
                            checked={newSourceEnabled}
                            onChange={e => setNewSourceEnabled(e.target.checked)}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={2} className="text-end">
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-100"
                          onClick={handleAddIngestionSource}
                          disabled={isAddingSource || !selectedNewSourceOrgId}
                        >
                          {isAddingSource ? (
                            <Spinner animation="border" size="sm" className="me-1" />
                          ) : (
                            <i className="fas fa-plus me-1" />
                          )}
                          {t("Agregar")}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Card 2: List of Configured Sources */}
                <Card className="border shadow-sm">
                  <Card.Header className="bg-white fw-semibold d-flex align-items-center justify-content-between py-2">
                    <div>
                      <i className="fas fa-network-wired me-2 text-danger" />
                      {t("Fuentes de Ingesta Configuradas")} ({ingestionSources.length})
                    </div>
                    {ingestionSources.length > 0 && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleTriggerIngestion()}
                        disabled={triggeringSourceId !== null || ingestionSources.filter(s => s.enabled).length === 0}
                      >
                        {triggeringSourceId === "all" ? (
                          <Spinner animation="border" size="sm" className="me-1" />
                        ) : (
                          <i className="fas fa-bolt me-1 text-warning" />
                        )}
                        {t("Ejecutar Todas las Activas")}
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body className="p-0 small">
                    {isLoadingIngestion ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        {t("Cargando fuentes de ingesta...")}
                      </div>
                    ) : ingestionSources.length === 0 ? (
                      <div className="text-center text-muted py-4 px-3">
                        <i className="fas fa-inbox fs-3 d-block mb-2 text-secondary" />
                        {t("No hay organizaciones de origen configuradas.")}
                        <div className="small mt-1 text-muted">
                          {t("Puedes vincular tantas organizaciones de origen como desees usando el selector de arriba.")}
                        </div>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("Organización Origen")}</th>
                              <th className="text-center">{t("Estado")}</th>
                              <th className="text-center">{t("Puntos Ingestados")}</th>
                              <th>{t("Última Ingesta")}</th>
                              <th className="text-end">{t("Acciones")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ingestionSources.map((source) => {
                              const matchingOrg = allOrgs.find(o => o.id?.toLowerCase() === source.sourceOrganizationId?.toLowerCase())
                              const sourceDisplayName = matchingOrg ? matchingOrg.name : source.sourceOrganizationId
                              const isThisToggling = togglingSourceId === source.sourceOrganizationId
                              const isThisDeleting = deletingSourceId === source.sourceOrganizationId
                              const isThisTriggering = triggeringSourceId === source.sourceOrganizationId

                              return (
                                <tr key={source.sourceOrganizationId}>
                                  <td>
                                    <div className="fw-semibold">{sourceDisplayName}</div>
                                    <Badge bg="light" text="dark" className="border">
                                      ID: {source.sourceOrganizationId}
                                    </Badge>
                                  </td>
                                  <td className="text-center">
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      <Form.Check
                                        type="switch"
                                        id={`switch-${source.sourceOrganizationId}`}
                                        checked={source.enabled}
                                        disabled={isThisToggling}
                                        onChange={() => handleToggleIngestionSource(source.sourceOrganizationId, source.enabled)}
                                      />
                                      <Badge bg={source.enabled ? "success" : "secondary"}>
                                        {source.enabled ? t("Activa") : t("Pausada")}
                                      </Badge>
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <strong className="text-primary fs-6">{source.totalIngestedCount || 0}</strong>
                                  </td>
                                  <td>
                                    {source.lastIngestionAt ? (
                                      <div>
                                        <i className="far fa-clock me-1 text-muted" />
                                        {moment(source.lastIngestionAt).format("DD/MM/YYYY HH:mm")}
                                      </div>
                                    ) : (
                                      <span className="text-muted fst-italic">{t("Nunca ejecutada")}</span>
                                    )}
                                  </td>
                                  <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        title={t("Sincronizar ahora")}
                                        disabled={isThisTriggering || triggeringSourceId !== null}
                                        onClick={() => handleTriggerIngestion(source.sourceOrganizationId)}
                                      >
                                        {isThisTriggering ? (
                                          <Spinner animation="border" size="sm" />
                                        ) : (
                                          <i className="fas fa-bolt text-warning" />
                                        )}
                                      </Button>

                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        title={t("Eliminar fuente de ingesta")}
                                        disabled={isThisDeleting}
                                        onClick={() => handleDeleteIngestionSource(source.sourceOrganizationId, sourceDisplayName)}
                                      >
                                        {isThisDeleting ? (
                                          <Spinner animation="border" size="sm" />
                                        ) : (
                                          <i className="fas fa-trash-alt" />
                                        )}
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                  {ingestionSources.length > 0 && (
                    <Card.Footer className="bg-light d-flex justify-content-between align-items-center py-2 small text-muted">
                      <div>
                        <i className="fas fa-layer-group me-1 text-primary" />
                        {t("Total global ingestados:")}{" "}
                        <strong>
                          {ingestionSources.reduce((acc, s) => acc + (s.totalIngestedCount || 0), 0)}
                        </strong>
                      </div>
                      <div>
                        {t("Fuentes activas:")}{" "}
                        <strong>{ingestionSources.filter(s => s.enabled).length} de {ingestionSources.length}</strong>
                      </div>
                    </Card.Footer>
                  )}
                </Card>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className="bg-light py-2">
          <Button variant="secondary" size="sm" onClick={onHide}>
            {t("Cerrar")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal User Form */}
      <Modal show={showUserFormModal} onHide={() => setShowUserFormModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">
            {editingUser ? t("Editar Usuario") : t("Nuevo Usuario para la Organización")}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveUser}>
          <Modal.Body className="small">
            {userFormError && <Alert variant="danger">{userFormError}</Alert>}
            <Row className="g-2">
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="fw-semibold">{t("Nombre")}</Form.Label>
                  <Form.Control size="sm" value={firstName} onChange={e => setFirstName(e.target.value)} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="fw-semibold">{t("Apellido")}</Form.Label>
                  <Form.Control size="sm" value={lastName} onChange={e => setLastName(e.target.value)} required />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Label className="fw-semibold">{t("Correo Electrónico")}</Form.Label>
              <Form.Control type="email" size="sm" value={email} onChange={e => setEmail(e.target.value)} required />
            </Form.Group>

            {!editingUser && (
              <Form.Group className="mb-2">
                <Form.Label className="fw-semibold">{t("Contraseña")}</Form.Label>
                <Form.Control type="password" size="sm" value={password} onChange={e => setPassword(e.target.value)} required />
              </Form.Group>
            )}

            <Row className="g-2">
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="fw-semibold">{t("Rol")}</Form.Label>
                  <Form.Select size="sm" value={selectedRole} onChange={e => setSelectedRole(e.target.value)}>
                    <option value="Admin">Admin</option>
                    <option value="Agent">Agent</option>
                    <option value="Supervisor">Supervisor</option>
                    <option value="Observer">Observer</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="fw-semibold d-block">{t("Estado de la Cuenta")}</Form.Label>
                  <Form.Check
                    type="switch"
                    id="user-active-switch"
                    label={isActive ? t("Activa") : t("Inactiva")}
                    checked={isActive}
                    onChange={e => setIsActive(e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="py-2">
            <Button variant="secondary" size="sm" onClick={() => setShowUserFormModal(false)}>
              {t("Cancelar")}
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isSavingUser}>
              {isSavingUser ? <Spinner animation="border" size="sm" /> : t("Guardar Usuario")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Role Form */}
      <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">
            {editingRole ? t("Editar Rol de la Organización") : t("Nuevo Rol para la Organización")}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveRole}>
          <Modal.Body className="small">
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">{t("Nombre del Rol")}</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Ej. Vendedor, Inspector, Auditor"
                value={roleName}
                onChange={e => setRoleName(e.target.value)}
                required
              />
            </Form.Group>

            <h6 className="fw-bold mb-2 text-dark border-bottom pb-1">{t("Permisos Asignados")}</h6>
            <Row className="g-2">
              <Col xs={6}>
                <Form.Check
                  type="switch"
                  id="perm-customers"
                  label={t("Permiso Clientes")}
                  checked={permCustomers === 1}
                  onChange={e => setPermCustomers(e.target.checked ? 1 : 0)}
                />
              </Col>
              <Col xs={6}>
                <Form.Check
                  type="switch"
                  id="perm-invoice"
                  label={t("Permiso Facturación")}
                  checked={permInvoice === 1}
                  onChange={e => setPermInvoice(e.target.checked ? 1 : 0)}
                />
              </Col>
              <Col xs={6}>
                <Form.Check
                  type="switch"
                  id="perm-quotes"
                  label={t("Permiso Cotizaciones")}
                  checked={permQuotes === 1}
                  onChange={e => setPermQuotes(e.target.checked ? 1 : 0)}
                />
              </Col>
              <Col xs={6}>
                <Form.Check
                  type="switch"
                  id="perm-inventory"
                  label={t("Permiso Inventario")}
                  checked={permInventory === 1}
                  onChange={e => setPermInventory(e.target.checked ? 1 : 0)}
                />
              </Col>
              <Col xs={6}>
                <Form.Check
                  type="switch"
                  id="perm-reports"
                  label={t("Permiso Reportes")}
                  checked={permReports === 1}
                  onChange={e => setPermReports(e.target.checked ? 1 : 0)}
                />
              </Col>
              <Col xs={6}>
                <Form.Check
                  type="switch"
                  id="perm-users"
                  label={t("Permiso Usuarios Org.")}
                  checked={permUsers === 1}
                  onChange={e => setPermUsers(e.target.checked ? 1 : 0)}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="py-2">
            <Button variant="secondary" size="sm" onClick={() => setShowRoleModal(false)}>
              {t("Cancelar")}
            </Button>
            <Button variant="warning" size="sm" className="text-dark fw-semibold" type="submit" disabled={isSavingRole}>
              {isSavingRole ? <Spinner animation="border" size="sm" /> : t("Guardar Rol")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Preview Email */}
      <Modal show={!!previewEmail} onHide={() => setPreviewEmail(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">
            <i className="fas fa-envelope-open-text text-primary me-2" />
            {t("Detalles del Correo Enviado")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          {previewEmail && (
            <div>
              <div className="p-3 bg-light rounded-3 border mb-3">
                <Row className="g-2">
                  <Col md={6}>
                    <div className="text-muted mb-1">{t("Para / Destinatario")}:</div>
                    <Badge bg="light" text="dark" className="border font-monospace fs-6">
                      {previewEmail.to}
                    </Badge>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted mb-1">{t("De / Remitente")}:</div>
                    <div className="fw-semibold text-dark">{previewEmail.from || "Sistema Shearly"}</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted mb-1">{t("Fecha de Envío")}:</div>
                    <div className="fw-semibold text-dark">
                      <i className="far fa-clock me-1 text-primary" />
                      {previewEmail.date ? moment(previewEmail.date).format("DD/MM/YYYY HH:mm:ss") : "—"}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted mb-1">{t("Aperturas Registradas")}:</div>
                    <Badge bg={previewEmail.openCount && previewEmail.openCount > 0 ? "success" : "secondary"} pill>
                      {previewEmail.openCount || 0} {t("aperturas")}
                    </Badge>
                  </Col>
                  <Col xs={12} className="border-top pt-2 mt-2">
                    <div className="text-muted mb-1">{t("Asunto")}:</div>
                    <div className="fw-bold fs-6 text-dark">{previewEmail.subject || t("(Sin asunto)")}</div>
                  </Col>
                </Row>
              </div>

              {previewEmail.content && (
                <div>
                  <div className="fw-semibold text-dark mb-2">{t("Contenido del Mensaje")}:</div>
                  <div
                    className="p-3 border rounded-3 bg-white"
                    style={{ maxHeight: "400px", overflowY: "auto" }}
                    dangerouslySetInnerHTML={{ __html: previewEmail.content }}
                  />
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="secondary" size="sm" onClick={() => setPreviewEmail(null)}>
            {t("Cerrar")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default OrgDetailsModal
